// Travel Boards Service - Frontend API client
// Manages inspiration boards that can contain activities, trips, albums, and collections

import { API_BASE } from '../config/api';
import AuthService from './authService';

class TravelBoardsServiceDB {
  static getAuthHeaders() {
    return {
      ...AuthService.getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }

  static async getCurrentUserId() {
    const user = await AuthService.getCurrentUser();
    return user?.id || null;
  }

  // ==================== BOARD OPERATIONS ====================

  // Get all boards for the current user
  static async getUserBoards(includeShared = true) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/user/${userId}?includeShared=${includeShared}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get boards' };
      }

      const boards = await response.json();
      return { success: true, boards };
    } catch (error) {
      console.error('Error getting user boards:', error);
      return { success: false, error: 'Failed to get boards' };
    }
  }

  // Get a single board by ID
  static async getBoard(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/travel-boards/${boardId}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get board' };
      }

      const board = await response.json();
      return { success: true, board };
    } catch (error) {
      console.error('Error getting board:', error);
      return { success: false, error: 'Failed to get board' };
    }
  }

  // Get board by share code
  static async getBoardByShareCode(shareCode) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/travel-boards/share/${shareCode}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get board' };
      }

      const board = await response.json();
      return { success: true, board };
    } catch (error) {
      console.error('Error getting board by share code:', error);
      return { success: false, error: 'Failed to get board' };
    }
  }

  // Create a new board
  static async createBoard(name, description = null, visibility = 'private') {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, name, description, visibility })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to create board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating board:', error);
      return { success: false, error: 'Failed to create board' };
    }
  }

  // Update a board
  static async updateBoard(boardId, updates) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...updates })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating board:', error);
      return { success: false, error: 'Failed to update board' };
    }
  }

  // Delete a board
  static async deleteBoard(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to delete board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting board:', error);
      return { success: false, error: 'Failed to delete board' };
    }
  }

  // ==================== BOARD ITEMS OPERATIONS ====================

  // Get items in a board (supports pagination)
  static async getBoardItems(boardId, limit = null, offset = 0) {
    try {
      const userId = await this.getCurrentUserId();
      let url = `${API_BASE}/api/travel-boards/${boardId}/items?userId=${userId}`;

      // Add pagination params if specified
      if (limit !== null) {
        url += `&limit=${limit}&offset=${offset}`;
      }

      const response = await fetch(url, { headers: await this.getAuthHeaders() });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get board items' };
      }

      const data = await response.json();
      return {
        success: true,
        items: data.items,
        board: data.board,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Error getting board items:', error);
      return { success: false, error: 'Failed to get board items' };
    }
  }

  // Add item to board
  static async addItemToBoard(boardId, contentType, contentId, notes = null) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}/items`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, contentType, contentId, notes })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to add item' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding item to board:', error);
      return { success: false, error: 'Failed to add item' };
    }
  }

  // Remove item from board
  static async removeItemFromBoard(boardId, contentType, contentId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/${boardId}/items/${contentType}/${contentId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ userId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove item' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing item from board:', error);
      return { success: false, error: 'Failed to remove item' };
    }
  }

  // Remove an item from every board the current user owns — "Remove from
  // saved". Boards they only collaborate on are left alone.
  static async removeItemFromAllMyBoards(contentType, contentId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/travel-boards/items/${contentType}/${contentId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove item' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing item from boards:', error);
      return { success: false, error: 'Failed to remove item' };
    }
  }

  // Update item notes/order
  static async updateBoardItem(boardId, contentType, contentId, updates) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/${boardId}/items/${contentType}/${contentId}`,
        {
          method: 'PUT',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ userId, ...updates })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update item' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating board item:', error);
      return { success: false, error: 'Failed to update item' };
    }
  }

  // ==================== BOARD MEMBERS OPERATIONS ====================

  // Get board members
  static async getBoardMembers(boardId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/travel-boards/${boardId}/members`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get members' };
      }

      const members = await response.json();
      return { success: true, members };
    } catch (error) {
      console.error('Error getting board members:', error);
      return { success: false, error: 'Failed to get members' };
    }
  }

  // Add member to board
  static async addBoardMember(boardId, memberUserId, role = 'viewer') {
    try {
      const ownerId = await this.getCurrentUserId();
      if (!ownerId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}/members`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ ownerId, memberUserId, role })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to add member' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding board member:', error);
      return { success: false, error: 'Failed to add member' };
    }
  }

  // Remove member from board
  static async removeBoardMember(boardId, memberUserId) {
    try {
      const ownerId = await this.getCurrentUserId();
      if (!ownerId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/${boardId}/members/${memberUserId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ ownerId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove member' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing board member:', error);
      return { success: false, error: 'Failed to remove member' };
    }
  }

  // ==================== UTILITY OPERATIONS ====================

  // Get boards containing a specific item
  static async getBoardsContainingItem(contentType, contentId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/containing/${contentType}/${contentId}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get boards' };
      }

      const boards = await response.json();
      return { success: true, boards };
    } catch (error) {
      console.error('Error getting boards containing item:', error);
      return { success: false, error: 'Failed to get boards' };
    }
  }

  // Check if item is saved to any board
  static async isItemSaved(contentType, contentId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/is-saved/${contentType}/${contentId}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to check saved status' };
      }

      const result = await response.json();
      return { success: true, isSaved: result.isSaved };
    } catch (error) {
      console.error('Error checking saved status:', error);
      return { success: false, error: 'Failed to check saved status' };
    }
  }

  // Get all activity IDs saved to any of user's boards (for filtering uncategorized)
  static async getAllBoardItemIds(contentType = 'activity') {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/all-item-ids/${userId}?contentType=${contentType}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get board item IDs' };
      }

      const result = await response.json();
      return { success: true, contentIds: result.contentIds || [] };
    } catch (error) {
      console.error('Error getting all board item IDs:', error);
      return { success: false, error: 'Failed to get board item IDs' };
    }
  }

  // ==================== SHARING OPERATIONS ====================

  // Enable sharing for a board - generates share code
  static async shareBoard(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/share-board/${boardId}`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to share board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error sharing board:', error);
      return { success: false, error: 'Failed to share board' };
    }
  }

  // Get sharing settings for a board
  static async getBoardSharing(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/sharing/${boardId}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get sharing settings' };
      }

      const sharing = await response.json();
      return { success: true, sharing };
    } catch (error) {
      console.error('Error getting board sharing settings:', error);
      return { success: false, error: 'Failed to get sharing settings' };
    }
  }

  // Update sharing settings for a board
  static async updateBoardSharing(boardId, settings) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/sharing/${boardId}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...settings })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update sharing settings' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating board sharing:', error);
      return { success: false, error: 'Failed to update sharing settings' };
    }
  }

  // Disable sharing for a board
  static async disableBoardSharing(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/sharing/${boardId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to disable sharing' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error disabling board sharing:', error);
      return { success: false, error: 'Failed to disable sharing' };
    }
  }

  // Discover public/friends boards
  static async discoverBoards(limit = 20, offset = 0) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/discover/${userId}?limit=${limit}&offset=${offset}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to discover boards' };
      }

      const boards = await response.json();
      return { success: true, boards };
    } catch (error) {
      console.error('Error discovering boards:', error);
      return { success: false, error: 'Failed to discover boards' };
    }
  }

  // ==================== HELPER METHODS ====================

  // Save item to multiple boards at once
  static async saveToBoards(contentType, contentId, boardIds) {
    const results = [];
    for (const boardId of boardIds) {
      const result = await this.addItemToBoard(boardId, contentType, contentId);
      results.push({ boardId, ...result });
    }
    return results;
  }

  // Remove item from multiple boards at once
  static async removeFromBoards(contentType, contentId, boardIds) {
    const results = [];
    for (const boardId of boardIds) {
      const result = await this.removeItemFromBoard(boardId, contentType, contentId);
      results.push({ boardId, ...result });
    }
    return results;
  }

  // Get board preview for social feed carousel (first 6 items with type breakdown)
  // `surface: 'list'` for a card (the listing gate); the board's page omits it.
  static async getBoardPreview(boardId, { surface } = {}) {
    try {
      const userId = await this.getCurrentUserId();
      const params = new URLSearchParams();
      if (userId) params.set('userId', userId);
      if (surface === 'list') params.set('surface', 'list');
      const query = params.toString();

      const response = await fetch(
        `${API_BASE}/api/travel-boards/preview/${boardId}${query ? `?${query}` : ''}`,
        { headers: await this.getAuthHeaders() }
      );

      // A refusal reads as missing; the page shows its not-found state for it
      if (response.status === 404) return { success: false, notFound: true };
      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get board preview' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting board preview:', error);
      return { success: false, error: 'Failed to get board preview' };
    }
  }

  // ==================== SAVED BOARDS ====================
  // These methods handle saving/unsaving boards to user's profile (no nesting)

  // Save a board to user's saved boards
  static async saveBoard(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/save-board/${boardId}`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to save board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving board:', error);
      return { success: false, error: 'Failed to save board' };
    }
  }

  // Unsave a board from user's saved boards
  static async unsaveBoard(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/travel-boards/save-board/${boardId}?userId=${userId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to unsave board' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error unsaving board:', error);
      return { success: false, error: 'Failed to unsave board' };
    }
  }

  // Check if a board is saved by user
  static async isBoardSaved(boardId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/travel-boards/is-board-saved/${boardId}?userId=${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to check saved status' };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error checking if board is saved:', error);
      return { success: false, error: 'Failed to check saved status' };
    }
  }

  // Archive a board
  // makePrivate is the opt-in hard cut — see CollectionsService.archiveCollection.
  static async archiveBoard(boardId, makePrivate = false) {
    try {
      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}/archive`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ make_private: makePrivate })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to archive board' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error archiving board:', error);
      return { success: false, error: 'Failed to archive board' };
    }
  }

  // Unarchive (restore) a board
  static async unarchiveBoard(boardId) {
    try {
      const response = await fetch(`${API_BASE}/api/travel-boards/${boardId}/unarchive`, {
        method: 'PUT',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to restore board' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error unarchiving board:', error);
      return { success: false, error: 'Failed to restore board' };
    }
  }
}

export default TravelBoardsServiceDB;
